import {useEffect, useRef, useState} from "react";
import {clsx} from "clsx";
import type {PDFDocumentLoadingTask, RenderTask} from "pdfjs-dist";
import {Alert} from "@/primitives/Alert";
import {Button} from "@/primitives/Button";
import {FA6Icon} from "@/primitives/FA6Icon";
import {Modal} from "@/primitives/Modal";
import {Select} from "@/primitives/Select";
import {loadPdfJs, mergePdfs} from "./pdfjs";
import type {LoadState} from "./types";
import {useExportPreview} from "./useExportPreview";
import styles from "./PdfPreviewModal.module.css";

export interface PdfPreviewModalProps {
	title: string;
	exportJobId: string;
	loadingMessage?: string;
}

const FALLBACK_PAGE_WIDTH = 860;

// a copy, pdf.js may transfer the original buffer to its worker
const pdfBlob = (data: Uint8Array) => new Blob([data.slice()], {type: "application/pdf"});

/**
 * Renders every page of a PDF as a canvas into the container, fitted to its width.
 * @return a function that cancels the rendering
 */
function renderPdf(data: Uint8Array, container: HTMLElement, onPageRendered: () => void, onError: (error: unknown) => void): () => void {
	let cancelled = false;
	let loadingTask: PDFDocumentLoadingTask | null = null;
	let renderTask: RenderTask | null = null;

	const renderPages = async () => {
		const pdfJs = await loadPdfJs();
		if (cancelled) {
			return;
		}
		// pdf.js transfers the buffer to its worker, so it gets a copy to keep the document for the next visit and for printing
		loadingTask = pdfJs.getDocument({data: data.slice()});
		const pdf = await loadingTask.promise;
		const targetWidth = (container.clientWidth || FALLBACK_PAGE_WIDTH) * window.devicePixelRatio;

		for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
			const page = await pdf.getPage(pageNumber);
			if (cancelled) {
				return;
			}
			const viewport = page.getViewport({scale: targetWidth / page.getViewport({scale: 1}).width});
			const canvas = document.createElement("canvas");
			canvas.className = styles.page;
			canvas.width = Math.floor(viewport.width);
			canvas.height = Math.floor(viewport.height);
			container.append(canvas);

			renderTask = page.render({canvas, viewport});
			await renderTask.promise;
			onPageRendered();
		}
	};

	renderPages().catch((error: unknown) => {
		if (!cancelled) {
			onError(error);
		}
	});

	return () => {
		cancelled = true;
		renderTask?.cancel();
		void loadingTask?.destroy();
	};
}

function save(url: string, fileName: string) {
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	link.click();
}

/**
 * Shows the PDF of an export job once it is ready, or every PDF of a ZIP export to flip through. Meant to be driven
 * by a `ModalIsland`; the content below unmounts when the dialog closes, which stops loading and rendering and frees the buffers.
 */
export function PdfPreviewModal(props: PdfPreviewModalProps) {
	return (
		<Modal.Content size="xl" className={styles.dialog}>
			<PdfPreview {...props}/>
		</Modal.Content>
	);
}

function PdfPreview({title, exportJobId, loadingMessage}: PdfPreviewModalProps) {
	const bodyRef = useRef<HTMLDivElement>(null);
	const pagesRef = useRef<HTMLDivElement>(null);
	const printFrameRef = useRef<HTMLIFrameElement>(null);
	const {state: loadState, file, documents} = useExportPreview(exportJobId);
	const [renderState, setRenderState] = useState<LoadState>("loading");
	const [index, setIndex] = useState(0);

	const current = documents[index];

	useEffect(() => {
		if (current == null) {
			return;
		}
		pagesRef.current.replaceChildren();
		bodyRef.current.scrollTop = 0;
		setRenderState("loading");
		return renderPdf(current.data, pagesRef.current, () => setRenderState("loaded"), error => {
			console.error("Could not render PDF preview", error);
			setRenderState("error");
		});
	}, [current]);

	// printed and downloaded from its own blob URL, so the browser's PDF viewer prints the original file
	const [currentUrl, setCurrentUrl] = useState<string | null>(null);
	useEffect(() => {
		if (current == null) {
			return;
		}
		const url = URL.createObjectURL(pdfBlob(current.data));
		setCurrentUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [current]);

	// all time sheets merged into one PDF, created on the first "Print all"
	const allUrlRef = useRef<Promise<string> | null>(null);
	const [mergingAll, setMergingAll] = useState(false);
	const [printAllFailed, setPrintAllFailed] = useState(false);
	useEffect(() => () => {
		void allUrlRef.current?.then(url => URL.revokeObjectURL(url), (): void => undefined);
	}, []);

	const print = (url: string) => {
		const frame = printFrameRef.current;
		if (frame.src === url) {
			frame.contentWindow?.print();
		} else {
			frame.onload = () => frame.contentWindow?.print();
			frame.src = url;
		}
	};

	const printAll = () => {
		allUrlRef.current ??= mergePdfs(documents.map(document => document.data))
			.then(data => URL.createObjectURL(pdfBlob(data)));
		setMergingAll(true);
		setPrintAllFailed(false);
		allUrlRef.current
			.then(print)
			.catch((error: unknown) => {
				allUrlRef.current = null;
				console.error("Could not merge PDFs for printing", error);
				setPrintAllFailed(true);
			})
			.finally(() => setMergingAll(false));
	};

	const showDocument = (next: number) => setIndex(Math.min(Math.max(next, 0), documents.length - 1));

	const multiple = documents.length > 1;
	const failed = loadState === "error" || renderState === "error";

	return (
		<>
			<Modal.Header>
				<Modal.Title>{title}</Modal.Title>
				{multiple && (
					<div className={styles.toolbar}>
						<Button variant="secondary" onClick={() => showDocument(index - 1)} disabled={index === 0}
						        title={messages["datatable.paginate.previous"]} aria-label={messages["datatable.paginate.previous"]}>
							<FA6Icon icon="chevron-left" variant="regular" fixedWidth/>
						</Button>
						<div className={styles.documentSelect}>
							<Select.Root
								items={documents.map((document, documentIndex) => ({label: document.name, value: documentIndex}))}
								value={index}
								onValueChange={value => showDocument(value)}
							>
								<Select.Trigger/>
								<Select.Items/>
							</Select.Root>
						</div>
						<Button variant="secondary" onClick={() => showDocument(index + 1)} disabled={index === documents.length - 1}
						        title={messages["datatable.paginate.next"]} aria-label={messages["datatable.paginate.next"]}>
							<FA6Icon icon="chevron-right" variant="regular" fixedWidth/>
						</Button>
						<span className={styles.position}>{index + 1} / {documents.length}</span>
					</div>
				)}
			</Modal.Header>
			<Modal.Body className={styles.body} ref={bodyRef}>
				{!failed && (loadState === "loading" || renderState === "loading") && (
					<div className="text-center">
						<FA6Icon icon="spinner-third" variant="regular" className="spinner"/>
						{loadState === "loading" && loadingMessage && <div className="mt-10">{loadingMessage}</div>}
					</div>
				)}
				{(failed || printAllFailed) && (
					<Alert variant="danger" iconPosition="left">{messages["downloadStatus.error"]}</Alert>
				)}
				<div ref={pagesRef} className={styles.pages} hidden={failed}/>
				<iframe ref={printFrameRef} className={styles.printFrame} title={messages["dialog.print"]} aria-hidden tabIndex={-1}/>
			</Modal.Body>
			<Modal.Footer className={styles.footer}>
				<Button variant="secondary" className={clsx(!multiple && "mr-auto")} onClick={() => print(currentUrl)} disabled={currentUrl == null}>
					<FA6Icon icon="print" variant="regular" fixedWidth className="mr-5"/>
					{messages["dialog.print"]}
				</Button>
				{multiple && (
					<Button variant="secondary" className="mr-auto" onClick={printAll} disabled={mergingAll}>
						<FA6Icon icon={mergingAll ? "spinner-third" : "print"} variant="regular" fixedWidth className={clsx("mr-5", mergingAll && "spinner")}/>
						{messages["dialog.printAll"]}
					</Button>
				)}
				<Button variant={multiple ? "secondary" : "primary"} onClick={() => save(currentUrl, current.name)} disabled={currentUrl == null}>
					<FA6Icon icon="download" variant="regular" fixedWidth className="mr-5"/>
					{messages["dialog.download"]}
				</Button>
				{multiple && (
					<Button onClick={() => save(file.url, file.fileName)}>
						<FA6Icon icon="download" variant="regular" fixedWidth className="mr-5"/>
						{messages["dialog.downloadAll"]}
					</Button>
				)}
				<Modal.Close render={<Button variant="secondary" type="button"/>}>
					{messages["dialog.cancel"]}
				</Modal.Close>
			</Modal.Footer>
		</>
	);
}
